import {FC} from 'react';

import {Box, IconButton, styled, Typography, useMediaQuery} from '@mui/material';
import {useTranslation} from 'react-i18next';

import DeleteOutlineIcon2 from 'assets/icons/deleteOutlineIcon2';
import {Button} from 'components/button/Button';
import theme from 'config/theme';
import {BASKET_UPDATE_ITEM_SOURCE_EVENTS, BASKET_UPDATE_ITEM_TRIGGER_TYPE} from 'contexts/analytics-context';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {AnalyticsData, BasketLineItem, ProductType} from 'types';

import {
  ProductAttributesBox,
  StyledCard,
  StyledCardContent,
  StyledCardContentWrapper,
  StyledTitleTypography,
  StyledTitleTypographyWrapper,
  StyledProductCard
} from './styles';
import {WeightedFreshPerUnitPrice} from './WeightedFreshPerUnitPrice';
import {BasketProductImage} from '../../components/basket-product-image/BasketProductImage';

const DisabledButtonStyled = styled(Button)(() => ({
  fontSize: '0.75rem',
  '&.Mui-disabled': {
    color: 'black'
  }
})) as typeof Button;

export type BasketProductProps = {
  coverImageURL: string;
  title: string;
  weight: string;
  displayPrice: number;
  onQuantityChange: (qty: number, analyticsData?: AnalyticsData) => Promise<void>;
  productId: string;
  basketItem: BasketLineItem;
  loading: boolean;
  productUnavailableText?: string;
  productType: ProductType;
};

export const BasketProductV2: FC<BasketProductProps> = ({
  title,
  coverImageURL,
  onQuantityChange,
  weight,
  productId,
  basketItem,
  loading,
  productUnavailableText = '',
  productType
}) => {
  const {t} = useTranslation();

  const analyticsData = {
    sourceEvent: '',
    triggerType: '',
    productId: basketItem?.productId || '',
    productPrice: basketItem?.productDetails?.displayPrice || '',
    qty: 0
  };

  const isWeightedFresh = basketItem?.productDetails?.isWeightedFresh || false;
  const weightedFreshPerUnitPrice = basketItem?.productDetails?.weightedFreshPricePerUnit;
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const isMobileScreen = isReactNativeView || isMobileView;

  const handleDelete = async () => {
    analyticsData.sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM;
    analyticsData.triggerType = BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON;
    analyticsData.qty = 0;
    await onQuantityChange(0, analyticsData);
    SendBrowserEvent.showInfoToast(t('message.productRemoved'));
    // await deleteLineItem(variantId, basketItem, analyticsData, undefined);
  };

  return (
    <StyledCard data-test-product-id={productId} data-test-product-name={title} isReactNativeView={isMobileScreen}>
      <Box display="flex">
        <Box width="100%">
          <StyledProductCard isReactNativeView={isMobileScreen}>
            <Box
              onClick={() => {
                SendBrowserEvent.goToPDP(productId);
              }}
              sx={{opacity: '0.5'}}
            >
              <BasketProductImage
                src={coverImageURL}
                productType={productType}
                isBulky={basketItem.productDetails.isBulky}
              />
            </Box>

            <StyledCardContentWrapper role="button" tabIndex={0} onKeyDown={() => null}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <StyledCardContent
                  onClick={() => {
                    SendBrowserEvent.goToPDP(productId);
                  }}
                  isReactNativeView={isMobileScreen}
                  isProductUnavailable={true}
                >
                  <StyledTitleTypographyWrapper>
                    <StyledTitleTypography color="secondary" gutterBottom variant="body1" component="p">
                      <b>{title}</b>
                    </StyledTitleTypography>
                  </StyledTitleTypographyWrapper>
                  <ProductAttributesBox>
                    {isWeightedFresh && weightedFreshPerUnitPrice ? (
                      <WeightedFreshPerUnitPrice
                        perUnitPrice={weightedFreshPerUnitPrice}
                        size={weight}
                        isWeightedFreshV3Enabled={isWeightedFresh}
                      ></WeightedFreshPerUnitPrice>
                    ) : (
                      <Typography display={'block'} variant="caption" color="text.secondary">
                        {weight}
                      </Typography>
                    )}
                  </ProductAttributesBox>
                </StyledCardContent>

                <Box display="flex" flexDirection="column" alignItems="end" gap="10px">
                  {!isMobileScreen && (
                    <IconButton
                      data-test-delete-btn=""
                      disabled={loading}
                      type="button"
                      onClick={handleDelete}
                      size="small"
                    >
                      <DeleteOutlineIcon2 fillColor="#8E8E8E" fontSize="small" />
                    </IconButton>
                  )}

                  <DisabledButtonStyled
                    className={'leo'}
                    variant="contained"
                    shape="rectangle"
                    disableRipple={true}
                    disabled={true}
                    fullWidth
                    color="inherit"
                  >
                    <b>{productUnavailableText}</b>
                  </DisabledButtonStyled>
                </Box>
              </Box>
            </StyledCardContentWrapper>
          </StyledProductCard>
        </Box>
      </Box>
    </StyledCard>
  );
};

BasketProductV2.displayName = 'Product';

export default BasketProductV2;
